/**
 * EmailConfirmModal — May 4, 2026.
 *
 * Reusable "confirm recipient before send" gate. Shown before BOTH
 * the initial Tax Cert send (Complete button) and every Resend.
 *
 * Features (per DELIVERY-UX spec):
 *   • Recipient email rendered in a large monospace block
 *   • Inline editable input (no need to leave the modal)
 *   • Real-time soft typo warnings (TLD, provider, client domain)
 *   • Format check is the only blocker — typo warnings are warnings
 *   • Apply-suggestion buttons for one-click fixes
 *
 * Returns the confirmed email via onConfirm(email). Caller is
 * responsible for the actual API call + UI feedback.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { AlertTriangle, Check, Loader2, Mail, Pencil, Send } from 'lucide-react';
import {
  validateEmailFormat,
  detectEmailWarnings,
} from '../../utils/emailValidation';

function domainOf(email) {
  if (!email) return '';
  const at = email.lastIndexOf('@');
  return at >= 0 ? email.slice(at + 1).toLowerCase() : '';
}

const EmailConfirmModal = ({
  open,
  onOpenChange,
  initialEmail = '',
  knownEmail = '', // currently-configured-on-file email (for client-domain check)
  title = 'Confirm Recipient Email',
  description = 'The tax certificate will be sent to the email address below. Please verify before sending.',
  confirmLabel = 'Confirm & Send',
  busyLabel = 'Sending…',
  busy = false,
  onConfirm,
}) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initialEmail || '');
  const [acknowledgedWarning, setAcknowledgedWarning] = useState(false);
  const inputRef = useRef(null);

  // Reset internal state every time the modal opens — prevents stale
  // values when the same modal instance is re-used for back-to-back sends.
  useEffect(() => {
    if (open) {
      setValue(initialEmail || '');
      setEditing(false);
      setAcknowledgedWarning(false);
    }
  }, [open, initialEmail]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const trimmed = (value || '').trim();
  const isFormatValid = validateEmailFormat(trimmed);

  const knownDomain = useMemo(() => domainOf(knownEmail), [knownEmail]);

  const warnings = useMemo(() => {
    if (!trimmed) return [];
    return detectEmailWarnings(trimmed, { knownDomain, knownEmail });
  }, [trimmed, knownDomain, knownEmail]);

  // When the typed value changes, reset the "I know about this typo"
  // acknowledgement so each new edit re-prompts.
  useEffect(() => {
    setAcknowledgedWarning(false);
  }, [value]);

  const blocker = !isFormatValid;
  const hasWarnings = warnings.length > 0;
  const requiresAck = hasWarnings && !acknowledgedWarning;

  const handleApplySuggestion = (suggestion) => {
    setValue(suggestion);
    setEditing(true);
  };

  const handleConfirm = () => {
    if (blocker || requiresAck || busy) return;
    onConfirm?.(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange?.(o)}>
      <DialogContent className="sm:max-w-md" data-testid="email-confirm-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-blue-600" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {description}
          </DialogDescription>
        </DialogHeader>

        {/* Recipient block */}
        <div className="space-y-3 py-2">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                Recipient
              </span>
              {!editing && (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-700"
                  data-testid="email-confirm-edit-btn"
                >
                  <Pencil className="w-3 h-3" /> Edit
                </button>
              )}
            </div>
            {editing ? (
              <Input
                ref={inputRef}
                type="email"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !blocker && !requiresAck) {
                    e.preventDefault();
                    handleConfirm();
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setEditing(false);
                  }
                }}
                className="font-mono text-sm h-9"
                placeholder="recipient@example.com"
                data-testid="email-confirm-input"
              />
            ) : (
              <p
                className={`font-mono text-base font-medium break-all ${
                  isFormatValid ? 'text-slate-900' : 'text-red-600'
                }`}
                data-testid="email-confirm-display"
              >
                {trimmed || <span className="text-slate-400 italic">No email</span>}
              </p>
            )}
          </div>

          {/* Format error — blocking */}
          {!isFormatValid && trimmed && (
            <div
              className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 p-2"
              data-testid="email-confirm-format-error"
            >
              <AlertTriangle className="w-3.5 h-3.5 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-700">
                That doesn't look like a valid email address. Please correct
                before sending.
              </p>
            </div>
          )}

          {/* Soft warnings — non-blocking, require ack */}
          {hasWarnings && isFormatValid && (
            <div
              className="space-y-2"
              data-testid="email-confirm-warnings"
            >
              {warnings.map((w, idx) => (
                <div
                  key={`${w.type}-${idx}`}
                  className="rounded-md bg-amber-50 border border-amber-200 p-2"
                  data-testid={`email-confirm-warning-${w.type}`}
                >
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs text-amber-800">{w.message}</p>
                      {w.suggestion && (
                        <button
                          type="button"
                          onClick={() => handleApplySuggestion(w.suggestion)}
                          className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-amber-900 underline hover:text-amber-950"
                          data-testid={`email-confirm-apply-suggestion-${w.type}`}
                        >
                          Use <span className="font-mono">{w.suggestion}</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <label
                className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer pl-1"
                data-testid="email-confirm-ack-label"
              >
                <input
                  type="checkbox"
                  checked={acknowledgedWarning}
                  onChange={(e) => setAcknowledgedWarning(e.target.checked)}
                  className="rounded border-slate-300"
                  data-testid="email-confirm-ack-checkbox"
                />
                I have verified this email is correct.
              </label>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
            data-testid="email-confirm-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={blocker || requiresAck || busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="email-confirm-send-btn"
          >
            {busy ? (
              <>
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                {busyLabel}
              </>
            ) : (
              <>
                {hasWarnings ? (
                  <Check className="w-3.5 h-3.5 mr-1.5" />
                ) : (
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                )}
                {confirmLabel}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailConfirmModal;
